import React, { ReactElement, FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Typography, Steps, notification, Row, Col } from 'antd'
import { ShareAltOutlined } from '@ant-design/icons'
import { useGlobalAuth } from '@headbox-ui/auth-hook'
import { EnquiryDetails } from './EnquiryDetails'
import { Proposals } from './Proposals'
import { StepTitle } from './StepTitle'
import { RequestedProposal } from './RequestedProposal'
import { HeavyOutlineButton } from '../../../../Components/UI'
import { ROUTES } from '../../../../Constants'
import { useDateTimeDuration, useTimeSinceNow } from '../../../../Hooks'
import {
  SecondStepThreeQuarterIcon,
  ThirdStepNotStartedIcon,
  ThirdStepIcon,
} from './StepIcons'
import './style.less'
import { s } from './style'
import {
  InProgressBookingEventFragment,
  InProgressBookingProposalFragment,
} from './InProgressBooking.generated-types'
import { SuppliersWrapper } from '../../../../Components/Suppliers/SuppliersWrapper'
import { CompanyLogo } from './CompanyLogo'
import { SustainableEventBannerWrapper } from '../../../../Components/SustainableEventBannerWrapper'

const { Title, Text } = Typography
const { Step } = Steps

interface InProgressEventProps {
  event: InProgressBookingEventFragment
  requestedProposal: InProgressBookingProposalFragment
  refetchEvent: () => void
}

export const InProgressBooking: FC<InProgressEventProps> = ({
  event,
  event: { name, proposals, firstProposalsSentToGuestAt },
  requestedProposal,
  refetchEvent,
}): ReactElement => {
  const { user } = useGlobalAuth()
  const { t } = useTranslation()
  const { durationFullDate, durationRange } = useDateTimeDuration(event)
  const {
    medDate: submittedAtDate,
    time: submittedAtTimeString,
    timeSince: timeSinceSubmission,
  } = useTimeSinceNow(event.submittedAt)

  const firstProposalsSentToGuestDateTimeSince = useTimeSinceNow(
    firstProposalsSentToGuestAt
  )

  // Proposals are not set to rejected when the invitation is declined, so filter them out manually.
  // TODO: remove these when the backend is updated.
  const invitationIdsToInvitations = new Map(
    event.invitations.map((invitation) => [invitation.ID, invitation])
  )
  const filteredProposals = proposals.filter(
    (proposal) =>
      !invitationIdsToInvitations.get(proposal.invitationID)?.declinedAt
  )

  const hasProposals =
    filteredProposals.filter((p) => p.status === 'SentToGuest').length > 0

  const timeSinceEventSubmission = `${submittedAtDate}, ${submittedAtTimeString} (${timeSinceSubmission})`

  const formattedTimeSinceFirstProposalsSentToGuest =
    firstProposalsSentToGuestDateTimeSince.medDate &&
    firstProposalsSentToGuestDateTimeSince.time &&
    firstProposalsSentToGuestDateTimeSince.timeSince
      ? `${firstProposalsSentToGuestDateTimeSince.medDate}, ${firstProposalsSentToGuestDateTimeSince.time} (${firstProposalsSentToGuestDateTimeSince.timeSince})`
      : null

  const shareProposalsUrl = `${window.location.href}${ROUTES.ALL_PROPOSALS}/${event.UUID}`

  const awaitingResponsesStepInfo =
    'Our team is busy finding fantastic venues for you - you\'ll see their proposals here shortly.'

  const awaitingBookingStepInfo =
    'When you’ve chosen a venue, you can request to book and our team will be in touch to finalise the details.'

  const hasSupplierDeals =
    event.supplierDeals && event.supplierDeals.length > 0

  const handleShareNotification = async (): Promise<void> => {
    await navigator.clipboard.writeText(shareProposalsUrl)
    notification.success({
      message: (
        <Text style={s.notification.text}>
          {t('eventHub.inProgress.copied', 'Copied to clipboard')}
        </Text>
      ),
      placement: 'topLeft',
      duration: 2,
      style: s.notification.body,
    })
  }

  return (
    <div style={s.root}>
      <Row wrap={false} style={{ marginBottom: '32px' }}>
        <Col flex="auto">
          <Row>
            <Col>
              <Title
                level={3}
                style={{ lineHeight: '38px', marginBottom: '8px' }}
              >
                {name}
              </Title>
            </Col>
          </Row>
          <Row>
            <Col>
              <Text style={s.dateTime}>
                {durationFullDate}
                {durationRange ? ', ' + durationRange : ''}
              </Text>
            </Col>
          </Row>
        </Col>
        {Array.isArray(window.env.companyLogo) &&
          window.env.companyLogo.includes(user?.companyID) && (
          <Col flex="none">
            <CompanyLogo />
          </Col>
        )}
      </Row>
      <Steps
        className="in-progress-booking-steps"
        current={requestedProposal ? 2 : 1}
        direction="vertical"
      >
        <Step
          title={
            <StepTitle
              title={t(
                'eventHub.inProgress.enquirySubmitted',
                'Enquiry submitted'
              )}
              subtitle={timeSinceEventSubmission}
            />
          }
          description={
            <>
              <EnquiryDetails defaultExpanded={!hasProposals} event={event} />
              {!hasProposals && hasSupplierDeals ? (
                <SuppliersWrapper supplierDeals={event.supplierDeals} />
              ) : null}
            </>
          }
        />
        <Step
          title={
            <StepTitle
              title={
                hasProposals
                  ? 'Here are your venue options - time to review!'
                  : null
              }
              extra={
                hasProposals &&
                !requestedProposal && (
                  <HeavyOutlineButton
                    onClick={handleShareNotification}
                    style={s.button}
                  >
                    <ShareAltOutlined style={{ color: '#595959' }} />
                    <Text style={{ color: '#595959' }}>Share proposals</Text>
                  </HeavyOutlineButton>
                )
              }
              subtitle={
                hasProposals
                  ? formattedTimeSinceFirstProposalsSentToGuest
                  : null
              }
            />
          }
          description={
            hasProposals ? (
              <SustainableEventBannerWrapper
                eventUUID={event.UUID}
                marginLeft={12}
              >
                <div style={{ marginBottom: '24px' }}>
                  <Proposals
                    event={event}
                    defaultExpanded={!requestedProposal}
                    refetchEvent={refetchEvent}
                  />
                </div>
                {hasSupplierDeals ? (
                  <SuppliersWrapper supplierDeals={event.supplierDeals} />
                ) : null}
              </SustainableEventBannerWrapper>
            ) : (
              <SustainableEventBannerWrapper
                eventUUID={event.UUID}
                showIconInsideBanner={false}
                marginLeft={12}
              >
                <div>
                  <Text strong style={{ fontSize: '18px' }}>
                    {"We're working on it"}
                  </Text>
                  <div>
                    <Text style={{ fontSize: '14px' }}>
                      {formattedTimeSinceFirstProposalsSentToGuest}
                    </Text>
                  </div>
                  <Text
                    style={{
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      lineClamp: 2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      marginBottom: '24px',
                      marginTop: '8px',
                    }}
                  >
                    {awaitingResponsesStepInfo}
                  </Text>
                </div>
              </SustainableEventBannerWrapper>
            )
          }
          icon={<SecondStepThreeQuarterIcon style={{ width: '44px' }} />}
        />
        <Step
          icon={
            requestedProposal ? (
              <ThirdStepIcon style={{ width: '44px' }} />
            ) : (
              <ThirdStepNotStartedIcon style={{ width: '44px' }} />
            )
          }
          title={
            <StepTitle
              title={
                requestedProposal
                  ? t(
                    'eventHub.inProgress.waitingConfirmation',
                    'Waiting for confirmation'
                  )
                  : t(
                    'eventHub.inProgress.confirmBookingTitle',
                    'Confirm your booking'
                  )
              }
            />
          }
          description={
            requestedProposal ? (
              <RequestedProposal event={event} proposal={requestedProposal} />
            ) : (
              <div style={{ marginLeft: '12px' }}>
                <Text
                  style={{
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    lineClamp: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    marginBottom: '24px',
                    width: `${(100 * 2) / 3}%`,
                    marginTop: '8px',
                  }}
                >
                  {awaitingBookingStepInfo}
                </Text>
              </div>
            )
          }
        />
      </Steps>
    </div>
  )
}
